import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import type { inferOutput } from '@trpc/tanstack-react-query'
import { Alert, Modal } from 'antd'
import { z } from 'zod'
import { useStore } from 'zustand'
import { createListQueryStore } from '@/stores/list-query-store.ts'
import { useTRPC } from '@/trpc/react.ts'
import { createGroupBillColumns } from './group-bill-columns.tsx'

type Bill = inferOutput<ReturnType<typeof useTRPC>['bill']['candidates']>['data'][number]
const searchSchema = z.object({
  current: z.number().int().positive().catch(1),
  pageSize: z.number().int().min(1).max(100).catch(10),
  keyword: z.string().catch('')
})
const queryStore = createListQueryStore('finance:group-bill-picker', searchSchema)
export function GroupBillPicker({
  group,
  onClose,
  onSelect
}: {
  group: { id: number; organizationId: number; customerId: number }
  onClose: () => void
  onSelect: (bills: Bill[]) => void
}) {
  const trpc = useTRPC()
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const saved = useStore(queryStore, (state) => state.searches[currentSession.id])
  const search = saved ?? searchSchema.parse({})
  const setSearch = (value: z.infer<typeof searchSchema>) => queryStore.getState().remember(currentSession.id, value)
  const [selected, setSelected] = useState<Bill[]>([])
  const [open, setOpen] = useState(true)
  const q = useQuery({
    ...trpc.bill.candidates.queryOptions({
      ...search,
      organizationId: group.organizationId,
      customerId: group.customerId,
      editingGroupId: group.id,
      purpose: 'group'
    }),
    placeholderData: keepPreviousData
  })
  return (
    <Modal
      open={open}
      title="选择待收账单"
      width="65rem"
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      destroyOnHidden
      okButtonProps={{ disabled: !selected.length }}
      onOk={() => {
        onSelect(selected)
        setOpen(false)
      }}
    >
      {q.isError && <Alert type="error" title={q.error.message} />}
      <ProTable<Bill & { billId: number; billSnapshot: Bill }>
        rowKey="id"
        size="small"
        loading={q.isFetching}
        dataSource={q.data?.data.map((bill) => ({ ...bill, billId: bill.id, billSnapshot: bill })) ?? []}
        search={{ labelWidth: 'auto' }}
        form={{ initialValues: search }}
        toolBarRender={false}
        scroll={{ x: 'max-content' }}
        columnsState={{ persistenceKey: 'finance:group-bill-picker:table', persistenceType: 'localStorage' }}
        rowSelection={{
          selectedRowKeys: selected.map((b) => b.id),
          preserveSelectedRowKeys: true,
          onChange: (_, rows) => setSelected(rows.filter(Boolean))
        }}
        onSubmit={(v) => setSearch({ ...search, current: 1, keyword: String(v.keyword ?? '') })}
        onReset={() => setSearch({ current: 1, pageSize: 10, keyword: '' })}
        pagination={{
          current: search.current,
          pageSize: search.pageSize,
          total: q.data?.total,
          onChange: (current, pageSize) => setSearch({ ...search, current, pageSize })
        }}
        columns={[
          { title: '关键词 / ID', dataIndex: 'keyword', hideInTable: true },
          { title: 'ID', dataIndex: 'id', copyable: true, search: false },
          ...createGroupBillColumns<Bill & { billId: number; billSnapshot: Bill }>({
            amountField: 'outstandingAmount',
            amountTitle: '待收'
          })
        ]}
      />
    </Modal>
  )
}
